import { Link, useLocation } from "react-router-dom";
import {
  CaretDown, UserCircle, SignOut, ShieldCheck, List, Factory, ArrowSquareOut,
  FilePlus, Wrench, ArrowsLeftRight, FileText, FolderOpen, CalendarBlank,
  ChartLine, ChartPieSlice, Package, Buildings, UserPlus, CheckCircle,
  SquaresFour, UserSwitch, Truck, ClipboardText, Prohibit, UploadSimple,
  ArrowsClockwise, Pulse, MagnifyingGlass, FlowArrow, MapPin, Cube, ChartBar,
  ArrowCircleUp, Coins, Warning,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { useAuth } from "@/contexts/AuthContext";

// Labels follow global MRP/ERP naming conventions (SAP/Oracle/Infor-style
// module names) - renamed Feb 2026 as part of the "Materials Hub" rebrand.
// Reorganized Sep 4 2026, user's explicit ask: menu groups now match real
// ERP module boundaries (Procurement vs Supplier/Vendor lifecycle vs true
// Master Data vs Administration) instead of dumping supplier-portal admin
// actions under "Master Data" just because that's where they happened to
// land first. Routes are unchanged; only labels/grouping changed. `page`
// matches backend/auth_service.py's PAGE_CATALOG keys - a tab only
// renders if the signed-in user has that page.
const TABS = [
  { to: "/", label: "BOM Management", testId: "nav-bom-explorer", page: "bom_explorer" },
];

const PROCUREMENT_SUBTABS = [
  {
    subgroupLabel: "Purchase Orders",
    testId: "nav-procurement-sub-po",
    items: [
      { to: "/purchasing-strategy/purchase-order-create", label: "Create Purchase Order", testId: "nav-procurement-purchase-order-create", page: "purchase_order", icon: FilePlus },
      { to: "/purchasing-strategy/service-purchase-order-create", label: "Service Purchase Order", testId: "nav-procurement-service-purchase-order-create", page: ["purchase_order", "service_purchase_order"], icon: Wrench },
      { to: "/purchasing-strategy/interplant-purchase-order-create", label: "Interplant Purchase Order", testId: "nav-procurement-interplant-purchase-order-create", page: "interplant_purchase_order", icon: ArrowsLeftRight },
      { to: "/purchasing-strategy/created-purchase-orders", label: "Created POs", testId: "nav-procurement-created-purchase-orders", page: "created_purchase_orders", icon: FileText },
      { to: "/purchasing-strategy/open-purchase-orders", label: "Open Purchase Orders", testId: "nav-procurement-open-purchase-orders", page: "open_purchase_orders", icon: FolderOpen },
    ],
  },
  {
    subgroupLabel: "Planning & Allocation",
    testId: "nav-procurement-sub-planning",
    items: [
      { to: "/purchasing-plan", label: "Procurement Planning", testId: "nav-procurement-planning", page: "purchasing_plan", icon: CalendarBlank },
      { to: "/production-plan", label: "Production Plan", testId: "nav-procurement-production-plan", page: "production_plan", icon: ChartLine },
      { to: "/purchasing-strategy/quota-allocation", label: "Quota Allocation", testId: "nav-procurement-quota-allocation", page: "quota_allocation", icon: ChartPieSlice },
    ],
  },
  {
    subgroupLabel: "Goods Receipt",
    testId: "nav-procurement-sub-receipt",
    items: [
      { to: "/admin/grn-approval", label: "Vendor Goods Receipt", testId: "nav-procurement-vendor-goods-receipt", page: "vendor_goods_receipt", icon: Package },
      { to: "/admin/grn-variance-log", label: "GRN Variance Log", testId: "nav-procurement-grn-variance-log", page: "vendor_goods_receipt", icon: ClipboardText },
    ],
  },
];

const SUPPLIER_MANAGEMENT_SUBTABS = [
  {
    subgroupLabel: "Vendor Directory & Onboarding",
    testId: "nav-supplier-sub-directory",
    items: [
      { to: "/purchasing-strategy/supplier-master", label: "Supplier Master", testId: "nav-supplier-management-supplier-master", page: "supplier_master", icon: Buildings },
      { to: "/admin/supplier-onboarding-approvals", label: "New Supplier Approval", testId: "nav-supplier-management-onboarding-approvals", page: "supplier_onboarding_approval", icon: CheckCircle },
      { to: "/admin/supplier-portal-invite", label: "Invite Supplier", testId: "nav-supplier-management-supplier-portal-invite", page: "supplier_portal_invite", icon: UserPlus },
      { to: "/admin/supplier-portal-approvals", label: "Supplier Portal Approvals", testId: "nav-supplier-management-supplier-portal-approvals", page: ["supplier_portal_admin", "supplier_portal_documents"], icon: CheckCircle },
    ],
  },
  {
    subgroupLabel: "Portal & Actions",
    testId: "nav-supplier-sub-actions",
    items: [
      { to: "/supplier-portal/login", label: "Supplier Dashboard", testId: "nav-supplier-management-supplier-dashboard", page: "supplier_dashboard", icon: SquaresFour },
      { to: "/admin/act-as-supplier", label: "Act as Supplier", testId: "nav-supplier-management-act-as-supplier", page: "act_as_supplier", icon: UserSwitch },
      { to: "/admin/act-as-supplier-report", label: "Act as Supplier Report", testId: "nav-supplier-management-act-as-supplier-report", page: "act_as_supplier", icon: ClipboardText },
    ],
  },
];

const INVENTORY_SUBTABS = [
  {
    subgroupLabel: "Stock & Allocation",
    testId: "nav-inventory-sub-stock",
    items: [
      { to: "/inventory", label: "Stock Overview", testId: "nav-inventory-stock-overview", page: "inventory", icon: Package },
      { to: "/inventory/product-allocation", label: "Product Allocation", testId: "nav-inventory-product-allocation", page: "inventory", icon: ChartPieSlice },
    ],
  },
  {
    subgroupLabel: "Transfers & Issues",
    testId: "nav-inventory-sub-transfers",
    items: [
      { to: "/inventory/inter-plant-transfer", label: "Inter Plant Stock Transfer", testId: "nav-inventory-stock-transfer", page: "stock_transfer", icon: Truck },
      { to: "/inventory/inbound-receipts", label: "Inbound Receipt", testId: "nav-inventory-inbound-receipts", page: "inbound_stock_transfer", icon: FolderOpen },
      { to: "/storeapproval", label: "Store Goods Issue", testId: "nav-inventory-goods-issue", page: "store_approval", icon: ArrowCircleUp },
    ],
  },
];

const MASTER_DATA_SUBTABS = [
  {
    subgroupLabel: "Material Operations",
    testId: "nav-masterdata-sub-operations",
    items: [
      { to: "/admin", label: "Component Master", testId: "nav-master-data-component-master", page: "admin", icon: Cube },
      { to: "/admin/create-material", label: "Create Material", testId: "nav-master-data-create-material", page: "admin_create_material", icon: FilePlus },
      { to: "/admin/activate-material-site", label: "Activate Material Site", testId: "nav-master-data-activate-material-site", page: "admin_activate_material_site", icon: MapPin },
    ],
  },
  {
    subgroupLabel: "Reports & Exemptions",
    testId: "nav-masterdata-sub-reports",
    items: [
      { to: "/admin/l1-l2-report", label: "L1/L2 Item Report", testId: "nav-master-data-l1l2-report", page: "admin", icon: ChartBar },
      { to: "/admin/closing-inventory-report", label: "Closing Inventory Report", testId: "nav-master-data-closing-inventory-report", page: "admin", icon: ClipboardText },
      { to: "/admin/byproduct-exemptions", label: "By-Product Exemptions", testId: "nav-master-data-byproduct-exemptions", page: "admin", icon: Prohibit },
      { to: "/admin/monthly-consumption-monitor", label: "Monthly Consumption Monitor", testId: "nav-master-data-monthly-consumption-monitor", page: "admin", icon: Factory },
      { to: "/admin/material-valuation-activation", label: "Material Valuation Activation", testId: "nav-master-data-material-valuation-activation", page: "admin", icon: Coins },
    ],
  },
];

const ADMINISTRATION_SUBTABS = [
  {
    subgroupLabel: "Integrations & Sync",
    testId: "nav-admin-sub-sync",
    items: [
      { to: "/admin/sap-write", label: "SAP Write", testId: "nav-administration-sap-write", page: "admin_sap_write", icon: UploadSimple },
      { to: "/admin/data-sync-status", label: "Data Sync Status", testId: "nav-administration-data-sync-status", page: "data_sync_status", icon: ArrowsClockwise },
      { to: "/admin/webhook-health", label: "Webhook Health", testId: "nav-administration-webhook-health", page: "admin_webhook_health", icon: Pulse },
    ],
  },
  {
    subgroupLabel: "Diagnostics & Flow",
    testId: "nav-admin-sub-diagnostics",
    items: [
      { to: "/admin/sto-diagnostics", label: "STO Diagnostics", testId: "nav-administration-sto-diagnostics", page: "sto_diagnostics", icon: MagnifyingGlass },
      { to: "/admin/shipment-diagnostics", label: "Shipment Diagnostics", testId: "nav-administration-shipment-diagnostics", page: "shipment_diagnostics", icon: MagnifyingGlass },
      { to: "/admin/document-flow", label: "Document Flow", testId: "nav-administration-document-flow", page: "document_flow", icon: FlowArrow },
      // Feb 2026, user's explicit ask - no `page` key (the backend
      // route is hard-gated to admin/super_admin only, a tenant-wide
      // SAP scan, not a grantable page permission like its neighbors
      // above) - `adminOnly: true` instead, checked directly against
      // the signed-in user's role in visibleSubgroups below.
      { to: "/admin/grn-fulfillment-audit", label: "GRN Fulfillment Audit", testId: "nav-administration-grn-fulfillment-audit", adminOnly: true, icon: Warning },
    ],
  },
];

// Filters a group's subgroups down to only items the user has access to,
// then drops any subgroup left with zero items - used for both the
// desktop dropdown and the mobile accordion so they never disagree.
// Feb 2026: an item can set `adminOnly: true` instead of `page` when its
// backend route is hard-gated to admin/super_admin (not a grantable page
// permission) - checked against `isAdmin` directly.
const visibleSubgroups = (subgroups, hasPageAccess, isAdmin) =>
  subgroups
    .map((sg) => ({ ...sg, items: sg.items.filter((t) => (t.adminOnly ? isAdmin : (!t.page || hasPageAccess(t.page)))) }))
    .filter((sg) => sg.items.length > 0);

// Oct 9 2026, user's explicit ask: external link to the separate
// "Production App" - opens in a new tab so the current MRP session/
// nav state is never disturbed. Not a page/route in this app, so it's
// a plain <a>, not a react-router <Link>, and has no `page` permission
// gate - visible to every signed-in user on every page.
const PRODUCTION_APP_URL = "https://production.radishtechnologies.com/";

export const NavTabs = () => {
  const { pathname } = useLocation();
  const { user, hasPageAccess, logout } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const visibleTabs = TABS.filter((t) => hasPageAccess(t.page));

  const isActive = (subgroups) => subgroups.some((sg) => sg.items.some((t) => t.to === pathname));

  const dropdownGroups = [
    { key: "procurement", label: "Procurement", testId: "nav-procurement", active: isActive(PROCUREMENT_SUBTABS), subgroups: visibleSubgroups(PROCUREMENT_SUBTABS, hasPageAccess) },
    { key: "supplier-management", label: "Supplier Management", testId: "nav-supplier-management", active: isActive(SUPPLIER_MANAGEMENT_SUBTABS), subgroups: visibleSubgroups(SUPPLIER_MANAGEMENT_SUBTABS, hasPageAccess) },
    { key: "inventory", label: "Inventory Management", testId: "nav-inventory", active: isActive(INVENTORY_SUBTABS), subgroups: visibleSubgroups(INVENTORY_SUBTABS, hasPageAccess) },
    { key: "master-data", label: "Master Data", testId: "nav-master-data", active: isActive(MASTER_DATA_SUBTABS), subgroups: visibleSubgroups(MASTER_DATA_SUBTABS, hasPageAccess) },
    { key: "administration", label: "Administration", testId: "nav-administration", active: isActive(ADMINISTRATION_SUBTABS), subgroups: visibleSubgroups(ADMINISTRATION_SUBTABS, hasPageAccess, isAdmin) },
  ];

  return (
    <>
      {/* Desktop / large tablet nav - flat text items with active bottom-border
          underline (Oct 13 2026 restyle, user's explicit ask: imitate a
          reference app's flat-menu-item nav bar look, icon-per-dropdown-row,
          while keeping 100% of Materials Hub's own subdivisions/routes).
          Sep 15 2026 fix: tabs+dropdowns wrap onto a second line (instead
          of overflowing OR requiring horizontal scroll) when they don't
          fit in one row - user's explicit ask: never require left/right
          scrolling to see the main menu, and never overlap the user-menu
          or SAP/ERP status badges. Each page's <header> uses min-h-16 (was
          a fixed h-16) so it can grow to fit a second line when needed. */}
      <div className="hidden lg:flex items-center gap-0.5 w-full min-w-0">
      <div className="flex items-center flex-wrap gap-px min-w-0 flex-1">
      {visibleTabs.map((tab) => {
        const active = pathname === tab.to;
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={`shrink-0 flex items-center px-2.5 py-3.5 text-[13px] font-semibold font-heading tracking-wide border-b-2 transition-colors duration-150 ${
              active ? "border-white text-white bg-white/10" : "border-transparent text-white/85 hover:text-white hover:bg-white/10"
            }`}
            data-testid={tab.testId}
          >
            {tab.label}
          </Link>
        );
      })}
      {dropdownGroups.map((group) => group.subgroups.length > 0 && (
        <DropdownMenu key={group.key}>
          <DropdownMenuTrigger
            className={`shrink-0 flex items-center gap-1 px-2.5 py-3.5 text-[13px] font-semibold font-heading tracking-wide border-b-2 transition-colors duration-150 outline-none ${
              group.active ? "border-white text-white bg-white/10" : "border-transparent text-white/85 hover:text-white hover:bg-white/10"
            }`}
            data-testid={group.testId}
          >
            {group.label}
            <CaretDown size={10} weight="bold" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[250px] bg-white border border-[#D0D5DD] shadow-lg rounded-md py-1" data-testid={`${group.testId}-dropdown-content`}>
            {group.subgroups.map((sg, i) => (
              <div key={sg.subgroupLabel || i}>
                {group.subgroups.length > 1 && (
                  <>
                    {i > 0 && <DropdownMenuSeparator />}
                    <DropdownMenuLabel className="text-[11px] font-heading font-bold text-[#0E7C86] tracking-wide px-3 pt-2 pb-1 bg-slate-50/80 border-b border-slate-100">
                      {sg.subgroupLabel}
                    </DropdownMenuLabel>
                  </>
                )}
                {sg.items.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = pathname === tab.to;
                  return (
                    <DropdownMenuItem key={tab.to} asChild className="rounded-none">
                      <Link to={tab.to} className="w-full cursor-pointer flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-[#344054] hover:bg-[#F0F9FA] hover:text-[#0B6B74]" data-testid={tab.testId}>
                        {Icon && <Icon size={16} className="shrink-0 text-[#667085]" />}
                        <span className="flex-1 truncate">{tab.label}</span>
                        {isActive && (
                          <span
                            className="shrink-0 w-2 h-2 rounded-full bg-[#12B76A]"
                            title="Current page"
                            data-testid={`${tab.testId}-active-dot`}
                          />
                        )}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ))}
      </div>

      <a
        href={PRODUCTION_APP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 px-2.5 py-3.5 text-[13px] font-semibold font-heading tracking-wide border-b-2 border-transparent text-white/85 hover:text-white hover:bg-white/10 transition-colors duration-150 shrink-0"
        data-testid="nav-production-app-link"
      >
        <Factory size={16} weight="fill" />
        Production App
        <ArrowSquareOut size={13} weight="bold" />
      </a>

      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs font-medium text-white hover:bg-white/10 transition-colors duration-150 outline-none shrink-0"
            data-testid="nav-user-menu"
          >
            <UserCircle size={18} weight="fill" />
            {user.name || user.email}
            <CaretDown size={10} weight="bold" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[220px] bg-white border border-[#D0D5DD] shadow-lg rounded-md py-1" data-testid="nav-user-menu-dropdown-content">
            <div className="px-3 py-2 text-xs text-[#667085] truncate">{user.email}</div>
            <DropdownMenuSeparator />
            {(user.role === "super_admin" || user.role === "admin") && (
              <DropdownMenuItem asChild className="rounded-none">
                <Link to="/admin/access-management" className="w-full cursor-pointer flex items-center gap-2.5 px-3 py-2 text-xs" data-testid="nav-access-management">
                  <ShieldCheck size={16} weight="bold" />
                  Access Management
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={logout} className="cursor-pointer flex items-center gap-2.5 px-3 py-2 text-xs rounded-none text-[#B42318]" data-testid="nav-sign-out">
              <SignOut size={16} weight="bold" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      </div>

      {/* Mobile / tablet nav - hamburger trigger + off-canvas drawer */}
      <div className="flex lg:hidden items-center ml-auto">
        <Sheet>
          <SheetTrigger asChild>
            <button
              type="button"
              className="w-11 h-11 flex items-center justify-center rounded-lg text-white hover:bg-white/15 transition-colors duration-150 shrink-0"
              data-testid="mobile-menu-trigger"
              aria-label="Open menu"
            >
              <List size={22} weight="bold" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-[85vw] max-w-sm p-0 flex flex-col bg-white z-[60]"
            data-testid="mobile-nav-drawer"
          >
            <SheetHeader className="px-4 pt-5 pb-3 border-b border-[#EAECF0] text-left">
              <SheetTitle className="text-[#0E7C86] font-heading">Materials Hub</SheetTitle>
            </SheetHeader>

            <div className="px-2 pt-2">
              <a
                href={PRODUCTION_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 min-h-11 px-3 rounded-lg text-[14px] font-bold font-heading text-[#0B6B74] bg-[#0E7C86]/10 hover:bg-[#0E7C86]/15 transition-colors duration-150"
                data-testid="mobile-nav-production-app-link"
              >
                <Factory size={18} weight="fill" />
                Production App
                <ArrowSquareOut size={14} weight="bold" className="ml-auto" />
              </a>
            </div>

            <div className="flex-1 overflow-y-auto px-2 py-2">
              {visibleTabs.map((tab) => {
                const active = pathname === tab.to;
                return (
                  <SheetClose asChild key={tab.to}>
                    <Link
                      to={tab.to}
                      className={`flex items-center min-h-11 px-3 rounded-lg text-[14px] font-bold font-heading transition-colors duration-150 ${
                        active ? "bg-[#0E7C86]/10 text-[#0B6B74]" : "text-[#344054] hover:bg-slate-100"
                      }`}
                      data-testid={`mobile-${tab.testId}`}
                    >
                      {tab.label}
                    </Link>
                  </SheetClose>
                );
              })}

              {dropdownGroups.some((g) => g.subgroups.length > 0) && (
                <Accordion type="multiple" className="mt-1">
                  {dropdownGroups.map((group) => group.subgroups.length > 0 && (
                    <AccordionItem key={group.key} value={group.key} className="border-b-0">
                      <AccordionTrigger
                        className={`px-3 min-h-11 text-[14px] font-bold font-heading no-underline hover:no-underline ${
                          group.active ? "text-[#0B6B74]" : "text-[#344054]"
                        }`}
                        data-testid={`mobile-${group.testId}`}
                      >
                        {group.label}
                      </AccordionTrigger>
                      <AccordionContent className="pl-3">
                        {group.subgroups.map((sg, i) => (
                          <div key={sg.subgroupLabel || i} className={i > 0 ? "mt-2" : ""}>
                            {group.subgroups.length > 1 && (
                              <div className="px-3 pb-1 text-[11px] font-heading font-bold text-[#0E7C86] tracking-wide">
                                {sg.subgroupLabel}
                              </div>
                            )}
                            {sg.items.map((tab) => {
                              const Icon = tab.icon;
                              return (
                                <SheetClose asChild key={tab.to}>
                                  <Link
                                    to={tab.to}
                                    className="flex items-center gap-2.5 min-h-11 px-3 rounded-lg text-[14px] font-medium text-[#475467] hover:bg-slate-100"
                                    data-testid={`mobile-${tab.testId}`}
                                  >
                                    {Icon && <Icon size={17} className="shrink-0 text-[#667085]" />}
                                    {tab.label}
                                  </Link>
                                </SheetClose>
                              );
                            })}
                          </div>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>

            {user && (
              <div className="border-t border-[#EAECF0] px-4 py-3">
                <div className="flex items-center gap-2 mb-2" data-testid="mobile-nav-user-info">
                  <UserCircle size={20} weight="fill" className="text-[#667085]" />
                  <div className="flex flex-col min-w-0">
                    <span className="text-[13px] font-bold text-[#1D2939] truncate">{user.name || user.email}</span>
                    <span className="text-[11px] text-[#667085] truncate">{user.email}</span>
                  </div>
                </div>
                {(user.role === "super_admin" || user.role === "admin") && (
                  <SheetClose asChild>
                    <Link
                      to="/admin/access-management"
                      className="flex items-center gap-2 min-h-11 px-3 rounded-lg text-[14px] font-bold text-[#344054] hover:bg-slate-100"
                      data-testid="mobile-nav-access-management"
                    >
                      <ShieldCheck size={16} weight="bold" />
                      Access Management
                    </Link>
                  </SheetClose>
                )}
                <SheetClose asChild>
                  <button
                    type="button"
                    onClick={logout}
                    className="flex items-center gap-2 min-h-11 px-3 rounded-lg text-[14px] font-bold text-[#B42318] hover:bg-red-50 w-full"
                    data-testid="mobile-nav-sign-out"
                  >
                    <SignOut size={16} weight="bold" />
                    Sign Out
                  </button>
                </SheetClose>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
};
